/* Phones: the photo is as tall as its content — headline, lead, and the
   search pill right under them (the Mobile board pinned a two-row search card
   to the bottom of a 560 px photo, which left a gap once the "When?" row and
   the long lead were gone). */
.hero {
  position: relative;
  /* Not overflow: hidden — the search suggestions hang below the hero; the
     photo and the veil are inset: 0, so nothing else overflows. z-index lifts
     the hero (and the list) over the category strip that follows. */
  z-index: 2;
  isolation: isolate;
  background: var(--hero-fallback);
  color: var(--text);
}

.photo {
  position: absolute;
  z-index: -1;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 66% 50%;
}

/* A cream veil over the top of the photo, so the dark headline and lead stay
   readable over the skyline (painted after the photo, still behind the text).
   --hero-veil-extend pushes its lower stops down when the hero is taller than
   its text — under the header (Page `underHeader`), which sets it. */
.hero::after {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(var(--veil-rgb), 0.92) 0%,
    rgba(var(--veil-rgb), 0.8) calc(40% + var(--hero-veil-extend, 0%)),
    rgba(var(--veil-rgb), 0.4) calc(56% + var(--hero-veil-extend, 0%)),
    rgba(var(--veil-rgb), 0) calc(68% + var(--hero-veil-extend, 0%))
  );
  pointer-events: none;
}

.inner {
  max-width: var(--container);
  margin: 0 auto;
  padding: 24px var(--gutter) 28px;
  box-sizing: border-box;
}

/* The site's line over the headline (`home.heroEyebrow`, optional). */
.eyebrow {
  margin: 0 0 12px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}

/* The headline's size and wrapping are the site's (--hero-title-*: its
   display font and copy set them). Dubai: one line on phones, the size scaled
   to the width it has — --hero-title-em is the title's width in em (Cormorant
   600, -0.02em tracking) plus a little slack: ~10.7 for EN / DE; the Russian
   title is shorter, so it caps at --hero-title-phone-max. */
.title {
  margin: 0;
  max-width: 640px;
  font-family: var(--font-display);
  font-weight: 600;
  font-size: min(var(--hero-title-phone-max), calc((100vw - 2 * var(--gutter)) / var(--hero-title-em)));
  white-space: var(--hero-title-phone-wrap);
  line-height: 0.95;
  text-shadow: 0 1px 12px rgba(var(--veil-rgb), 0.7);
  letter-spacing: -0.02em;
}

.lead {
  margin: 10px 0 0;
  max-width: 300px;
  font-family: var(--hero-lead-font);
  font-weight: var(--hero-lead-weight);
  font-size: var(--hero-lead-size-phone);
  line-height: 1.2;
  text-shadow: 0 1px 10px rgba(var(--veil-rgb), 0.8);
}

.searchBox {
  position: relative;
  z-index: 5;
  margin-top: 20px;
}

.form {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px;
  background: var(--card);
  border-radius: var(--radius-pill);
  box-shadow: 0 10px 30px rgba(var(--shadow-rgb), 0.2);
}

.field {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 4px 0 18px;
}

.input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  /* 16 px: iOS Safari zooms into any smaller field on focus. */
  font: 400 16px var(--font-body);
  color: var(--text);
}

/* The focus ring is the pill's, not the bare field's (the global
   :focus-visible outline drew a box inside it). */
.input:focus-visible {
  outline: none;
}

.form:focus-within {
  box-shadow:
    0 0 0 3px rgba(var(--primary-rgb), 0.28),
    0 10px 30px rgba(var(--shadow-rgb), 0.2);
}

.input::-webkit-search-cancel-button {
  cursor: pointer;
}

.input::placeholder {
  color: var(--text-muted);
}

.submit {
  height: 48px;
  padding: 0 16px;
  flex: none;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--primary);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font: 500 15px var(--font-body);
  cursor: pointer;
}

.submit:hover {
  background: var(--primary-hover);
}

/* The suggestion list: a card under the pill, over the content below. */
.suggestions {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  max-height: min(60vh, 420px);
  overflow-y: auto;
  background: var(--card);
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(var(--shadow-rgb), 0.22);
}

.suggestions[hidden] {
  display: none;
}

.suggestion {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  padding: 0 18px;
  font-size: 15px;
  color: var(--text);
  cursor: pointer;
}

.suggestion[aria-selected='true'] {
  background: var(--band);
}

/* A group's first row carries its heading ("Categories", "Popular searches"). */
.suggestion[data-section] {
  position: relative;
  margin-top: 30px;
}

.suggestion[data-section]::before {
  content: attr(data-section);
  position: absolute;
  left: 18px;
  bottom: 100%;
  padding-bottom: 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--label);
}

.suggestion[data-section]:first-child {
  margin-top: 26px;
}

.suggestionIcon {
  flex: none;
  color: var(--text-muted);
}

/* Narrow phones: a round icon-only button (the label stays for screen
   readers), so "What are you looking for?" isn't cut off. */
@media (max-width: 419px) {
  .submit {
    width: 48px;
    padding: 0;
  }

  .submitLabel {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
}

/* Main board (1440): the search pill sits under the lead. */
@media (min-width: 1024px) {
  .hero {
    min-height: 620px;
  }

  .photo {
    object-position: 60% 45%;
  }

  /* The text sits on the left: veil from the left instead of the top. */
  .hero::after {
    background: linear-gradient(
      90deg,
      rgba(var(--veil-rgb), 0.85) 0%,
      rgba(var(--veil-rgb), 0.6) 35%,
      rgba(var(--veil-rgb), 0) 60%
    );
  }

  .inner {
    padding: 120px var(--gutter) 96px;
  }

  .eyebrow {
    margin-bottom: 20px;
    font-size: 13px;
  }

  .title {
    font-size: var(--hero-title-size);
    white-space: normal;
  }

  .lead {
    margin-top: 22px;
    max-width: 460px;
    font-size: var(--hero-lead-size);
  }

  .searchBox {
    margin-top: 40px;
    max-width: 640px;
  }

  .form {
    gap: 8px;
    padding: 7px 7px 7px 26px;
    box-shadow: var(--shadow-search);
    box-sizing: border-box;
  }

  .form:focus-within {
    box-shadow:
      0 0 0 3px rgba(var(--primary-rgb), 0.28),
      var(--shadow-search);
  }

  .field {
    gap: 14px;
    padding: 0;
  }

  .submit {
    width: 104px;
    height: 56px;
    padding: 0;
  }

  .submitIcon {
    width: 22px;
    height: 22px;
  }

  .submitLabel {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
}
